import { cn } from "@/lib/utils";
import menuItems, { ChevronDown, type MenuItem } from "@/static/menuConfig";
import { useAuthStore } from "@/store";
import { LogOut } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAllDesignationsQuery } from "@/hooks/api/designation.queries";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
} from "./ui/sidebar";
import { Sparkles } from "lucide-react";

const AppSidebar = () => {
  const logout = useAuthStore((state) => state.logout);
  const user = useAuthStore((state) => state.user);
  const { pathname, search } = useLocation();
  const [openMenus, setOpenMenus] = useState<Set<string>>(new Set());

  const { data: designations = [] } = useAllDesignationsQuery({
    enabled: !!user?.permissions?.includes("designation.read"),
  });

  const dynamicMenuItems = useMemo(() => {
    return menuItems.map((item) => {
      if (item.title === "Admin") {
        const adminDesig = designations.find(
          (d) => d.name.toLowerCase() === "admin"
        );

        return {
          ...item,
          url: adminDesig
            ? `/people/staff?designation_id=${adminDesig.id}&designation_name=${adminDesig.name}`
            : item.url,
          items: undefined,
        };
      }
      return item;
    });
  }, [designations]);

  const hasPermission = (requiredPerms?: string[]) => {
    if (!requiredPerms || requiredPerms.length === 0) return true;
    return requiredPerms.some((p) => user?.permissions?.includes(p));
  };

  // Helper to check if a URL is active (exact or prefix match for detail pages)
  const isUrlActive = (url: string) => {
    if (url === "#") return false;

    const [urlPath, urlSearch] = url.split("?");

    // 1. If the url requires specific query parameters (like /people/staff?designation_id=...)
    // we enforce an exact match on pathname AND search to highlight the specific sub-item.
    if (urlSearch) {
      // Decode URI components in case search has special chars handled differently
      const decodedSearch = decodeURIComponent(search);
      const decodedTarget = decodeURIComponent(`?${urlSearch}`);
      return pathname === urlPath && decodedSearch === decodedTarget;
    }

    // 2. Exact match for regular paths
    if (pathname === url) return true;

    // 3. Prefix match for details pages (e.g. /activities/placement/123 matches /activities/placement)
    if (urlPath !== "/" && pathname.startsWith(urlPath + "/")) return true;

    return false;
  };

  // Auto-expand menu based on current pathname
  useEffect(() => {
    const titlesToOpen = new Set<string>();
    dynamicMenuItems.forEach((item) => {
      if (item.title === "People" && pathname.startsWith("/people/staff/")) {
        titlesToOpen.add("People");
      }
      if (item.items?.some((subItem) => isUrlActive(subItem.url))) {
        titlesToOpen.add(item.title);
      }
    });

    if (titlesToOpen.size > 0) {
      setOpenMenus((prev) => {
        const next = new Set(prev);
        titlesToOpen.forEach((title) => next.add(title));
        return next;
      });
    }
  }, [pathname, search]);

  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  const toggleMenu = useCallback((title: string) => {
    setOpenMenus((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(title)) {
        newSet.delete(title);
      } else {
        newSet.add(title);
      }
      return newSet;
    });
  }, []);

  const renderMenuItem = (item: MenuItem) => {
    if (!hasPermission(item.permissions)) return null;

    let displayItems = item.items;
    if (displayItems !== undefined) {
      displayItems = displayItems.filter((sub) =>
        hasPermission(sub.permissions)
      );
      if (displayItems.length === 0) return null;
    }

    const hasSubItems = displayItems && displayItems.length > 0;
    const isOpen = openMenus.has(item.title);

    const isChildActive = displayItems?.some((subItem) =>
      isUrlActive(subItem.url)
    );

    if (hasSubItems) {
      return (
        <SidebarMenuItem key={item.title}>
          <SidebarMenuButton
            onClick={() => toggleMenu(item.title)}
            className={cn(
              "group/menu-item h-11 px-4 transition-all duration-200 rounded-xl mb-1",
              isChildActive 
                ? "bg-indigo-50 text-indigo-700 font-bold border-l-4 border-indigo-600 ring-1 ring-indigo-100" 
                : "text-slate-600 hover:bg-slate-50 hover:text-indigo-600"
            )}
            isActive={isChildActive}
          >
            <item.icon className={cn("h-5 w-5 transition-transform duration-200 group-hover/menu-item:scale-110", isChildActive ? "text-indigo-600" : "text-slate-400")} />
            <span className="ml-2">{item.title}</span>
            <ChevronDown
              className={cn(
                "ml-auto h-4 w-4 transition-transform duration-200 text-slate-400",
                isOpen && "rotate-180",
                isChildActive && "text-indigo-600"
              )}
            />
          </SidebarMenuButton>
          {isOpen && (
            <SidebarMenuSub className="ml-4 border-l border-slate-100 pl-2 space-y-1 mt-1">
              {displayItems!.map((subItem) => (
                <SidebarMenuSubItem key={subItem.title}>
                  <NavLink
                    to={subItem.url}
                    className={({ isActive }) =>
                      cn(
                        "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all duration-200",
                        isActive 
                          ? "bg-indigo-600 text-white shadow-md font-semibold" 
                          : "text-slate-500 hover:bg-indigo-50 hover:text-indigo-600"
                      )
                    }
                  >
                    <span>{subItem.title}</span>
                  </NavLink>
                </SidebarMenuSubItem>
              ))}
            </SidebarMenuSub>
          )}
        </SidebarMenuItem>
      );
    }

    return (
      <SidebarMenuItem key={item.title}>
        <NavLink
          to={item.url}
          className={({ isActive }) =>
            cn(
              "flex w-full h-11 items-center gap-3 rounded-xl px-4 transition-all duration-200 mb-1",
              isActive 
                ? "bg-indigo-600 text-white shadow-lg shadow-indigo-100 font-bold" 
                : "text-slate-600 hover:bg-slate-50 hover:text-indigo-600"
            )
          }
        >
          {({ isActive }) => (
            <>
              <item.icon className={cn("h-5 w-5", isActive ? "text-white" : "text-slate-400")} />
              <span className="ml-2">{item.title}</span>
            </>
          )}
        </NavLink>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar className="border-r border-gray-100 bg-white/80 backdrop-blur-xl">
      <SidebarHeader className="px-6 py-6 h-20 flex flex-row items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-indigo-600 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-100">
          <Sparkles className="text-white h-6 w-6" />
        </div>
        <div>
          <h1 className="text-gray-900 text-xl font-black tracking-tight leading-none">Enturn</h1>
          <p className="text-indigo-600 text-[11px] font-bold uppercase tracking-widest mt-1">Student Hub</p>
        </div>
      </SidebarHeader>

      <SidebarContent className="px-3 py-4">
        <SidebarGroup>
          <SidebarGroupLabel className="sr-only">
            Navigation Links
          </SidebarGroupLabel>
          <SidebarMenu className="space-y-1">
            {dynamicMenuItems.map((item) => renderMenuItem(item))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t p-4">
        <SidebarMenuButton
          onClick={handleLogout}
          className="text-destructive hover:bg-destructive/10 hover:text-destructive w-full justify-start"
        >
          <LogOut className="mr-3 h-4 w-4" />
          Logout
        </SidebarMenuButton>
      </SidebarFooter>
    </Sidebar>
  );
};

export default AppSidebar;
